// src/plugins/sentryAxiosInterceptor.js - PRODUCTION VERSION
import axios from 'axios';
import * as Sentry from '@sentry/vue';

const isProduction = process.env.VUE_APP_ENVIRONMENT === 'production';

export function setupSentryAxiosInterceptor() {
  if (!isProduction) {
    return;
  }

  // REQUEST INTERCEPTOR
  axios.interceptors.request.use(
    (config) => {
      Sentry.setContext('http_request', {
        url: config.url,
        method: config.method?.toUpperCase(),
        baseURL: config.baseURL,
        timestamp: new Date().toISOString()
      });
      
      return config;
    },
    (error) => {
      Sentry.withScope((scope) => {
        scope.setTag('error_type', 'axios_request_error');
        scope.setLevel('warning');
        Sentry.captureException(error);
      });
      return Promise.reject(error);
    }
  );

  // RESPONSE INTERCEPTOR
  axios.interceptors.response.use(
    (response) => {
      Sentry.setContext('http_request', null);
      return response;
    },
    (error) => {
      if (error.response) {
        const status = error.response.status;
        
        // ONLY REPORT 5XX SERVER ERRORS
        if (status >= 500) {
          Sentry.withScope((scope) => {
            scope.setTag('error_type', 'server_error');
            scope.setTag('http_status', status.toString());
            scope.setTag('http_method', error.config?.method?.toUpperCase());
            
            scope.setContext('http_error', {
              url: error.config?.url,
              method: error.config?.method?.toUpperCase(),
              status: status,
              statusText: error.response.statusText,
              timestamp: new Date().toISOString()
            });
            
            const level = (status === 502 || status === 503 || status === 504) ? 'warning' : 'error';
            scope.setLevel(level);
            
            const errorMessage = `HTTP ${status} Error: ${error.config?.url}`;
            Sentry.captureMessage(errorMessage, level);
          });
        }
      }
      
      // NETWORK ERRORS (CONNECTION ISSUES)
      else if (!error.response && error.request) {
        Sentry.withScope((scope) => {
          scope.setTag('error_type', 'network_error');
          scope.setContext('network_error', {
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            timeout: error.config?.timeout,
            message: error.message,
            timestamp: new Date().toISOString()
          });
          
          scope.setLevel('error');
          Sentry.captureMessage(`Network Error: ${error.config?.url}`, 'error');
        });
      }
      
      return Promise.reject(error);
    }
  );
}

// MANUAL ERROR REPORTING
export function reportApiError(error, context = {}) {
  if (!isProduction) {
    return;
  }
  
  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'manual_api_error');
    
    Object.keys(context).forEach(key => {
      scope.setTag(key, context[key]);
    });
    
    scope.setLevel('error');
    Sentry.captureException(error);
  });
}